import { useRef, useEffect, type ReactNode } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2, Plus, X } from 'lucide-react'
import styled from 'styled-components'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'
import {
  Card,
  TopRow,
  DragHandle,
  IndexBadge,
  QuestionTextArea,
  DeleteButton,
} from '../../../../components/QuestionCardChrome/QuestionCardChrome.styles'
import type { InterviewQuestion } from '@bandos/shared'

// ─── Probe-editing styles (interview-specific) ────────────────────────────────

const ProbesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
  padding-left: 38px;
  padding-top: ${SPACING.sm};
  border-top: 1px solid var(--color-border);
`

const ProbeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
`

const ProbeArrow = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
  flex-shrink: 0;
  width: 14px;
  user-select: none;
`

const ProbeInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--color-border);
  border-radius: 0;
  padding: 3px 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-family: inherit;
  color: var(--color-text-secondary);
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-bottom-color: var(--color-primary);
    color: var(--color-text-primary);
  }

  &::placeholder {
    color: var(--color-text-disabled);
  }
`

const RemoveProbeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  border-radius: 4px;
  padding: 0;
  flex-shrink: 0;
  transition: color 0.15s ease;

  &:hover {
    color: #ef4444;
  }
`

const AddProbeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  cursor: pointer;
  padding: ${SPACING.xs} 0;
  margin-left: 14px;
  transition: color 0.15s ease;

  &:hover {
    color: var(--color-primary);
  }
`

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InterviewQuestionEditorProps {
  id: string
  question: InterviewQuestion
  index: number
  onChange: (q: InterviewQuestion) => void
  onDelete: () => void
  /** Optional side-note shown above the editor body (e.g. source attribution). */
  attribution?: ReactNode
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InterviewQuestionEditor({
  id,
  question,
  index,
  onChange,
  onDelete,
  attribution,
}: InterviewQuestionEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [question.question])

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange({ ...question, question: e.target.value })
  }

  const handleProbeChange = (probeIndex: number, value: string) => {
    const probes = [...(question.probes ?? [])]
    probes[probeIndex] = value
    onChange({ ...question, probes })
  }

  const handleAddProbe = () => {
    onChange({ ...question, probes: [...(question.probes ?? []), ''] })
  }

  const handleRemoveProbe = (probeIndex: number) => {
    const probes = (question.probes ?? []).filter((_, i) => i !== probeIndex)
    onChange({ ...question, probes })
  }

  const probes = question.probes ?? []

  return (
    <Card
      ref={setNodeRef}
      $isDragging={isDragging}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {attribution}
      <TopRow>
        <DragHandle
          type="button"
          {...listeners}
          {...attributes}
          title="Drag to reorder"
          tabIndex={-1}
        >
          <GripVertical size={14} />
        </DragHandle>
        <IndexBadge>{index + 1}.</IndexBadge>
        <QuestionTextArea
          ref={textareaRef}
          value={question.question}
          onChange={handleTextChange}
          placeholder="Enter your question…"
          rows={1}
        />
        <DeleteButton onClick={onDelete} title="Delete question">
          <Trash2 size={14} />
        </DeleteButton>
      </TopRow>

      <ProbesSection>
        {probes.map((probe, i) => (
          <ProbeRow key={i}>
            <ProbeArrow>↳</ProbeArrow>
            <ProbeInput
              value={probe}
              onChange={(e) => handleProbeChange(i, e.target.value)}
              placeholder="Follow-up probe if they give a short answer…"
            />
            <RemoveProbeButton
              type="button"
              onClick={() => handleRemoveProbe(i)}
              title="Remove probe"
            >
              <X size={12} />
            </RemoveProbeButton>
          </ProbeRow>
        ))}
        <AddProbeButton type="button" onClick={handleAddProbe}>
          <Plus size={12} />
          Add probe
        </AddProbeButton>
      </ProbesSection>
    </Card>
  )
}
